// GG Anvil Embers — ambient background sparks drifting upward.
// Dull orange forge embers rise slowly behind page content.
//
// Adapted from the standalone drop-in script (gg-anvil-embers.js) so it has
// a start/stop lifecycle instead of running forever from an IIFE. This is
// ONLY a shell change — the particle math, tuning knobs, and colors below
// are byte-identical to the source script. Do not "improve" them here.
//
// startEmbers() creates the canvas, starts the rAF loop, and returns a
// teardown function that cancels the loop, removes listeners, and removes
// the canvas from the DOM. Call the teardown on unmount (see
// components/AmbientEffects.tsx) — without it, navigating between public
// pages would stack a second canvas + a second rAF loop on top of the first.

export function startEmbers(): () => void {
  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return () => {}; // no ambient motion for users who opt out

  /* ============ TUNING KNOBS ============ */
  // Size/opacity bumped from the original (2026-07-03, matching the same
  // change in gganvil-site's copy) -- the as-authored values (0.8-2.2px,
  // 8-35% opacity) rendered real pixels (verified via canvas pixel data)
  // but were imperceptible against the dark page background in practice.
  // Density started at 40, was bumped to 100 when the effect was scoped to
  // just 4 public pages, then dialed back to 60 once it went app-wide
  // (every page, all the time) -- same per-ember visibility, less overall
  // clutter now that it's everywhere.
  const DENSITY = 60; // embers per 1920x1080 of screen (auto-scales)
  const SPEED_MIN = 0.15; // rise speed range (px per frame)
  const SPEED_MAX = 0.6;
  const SIZE_MIN = 1.5; // ember radius range (px)
  const SIZE_MAX = 4;
  const OPACITY_MIN = 0.18; // brightness range (keep low = "dullish")
  const OPACITY_MAX = 0.6;
  const FADE_TOP = 0.25; // fade out over top 25% of screen
  const COLORS = [
    // GG Anvil brand oranges, plus one deep ember
    '#F5821F',
    '#F79B57',
    '#FBB584',
    '#C85F14',
  ];
  /* ====================================== */

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  // z-index:-1 (as originally authored) sinks the canvas below the page's
  // OWN root background in real Chrome compositing -- confirmed empirically
  // (a 100%-opacity test circle drawn there never rendered on screen at
  // all, at any opacity/size). z-index:0 does NOT have that problem, but on
  // its own would paint ON TOP of #root's content if simply appended last.
  // Fix: z-index:0 AND insert as body's FIRST child (before #root) --
  // normal DOM-order stacking then keeps it behind the whole app, confirmed
  // empirically against the matching gganvil-site copy of this script (a
  // test circle rendered visibly clipped by content cards drawn after it).
  canvas.style.cssText =
    'position:fixed;inset:0;width:100vw;height:100vh;' + 'pointer-events:none;z-index:0;';
  document.body.insertBefore(canvas, document.body.firstChild);

  const ctx = canvas.getContext('2d')!;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  type Ember = {
    x: number;
    y: number;
    vy: number;
    sway: number;
    phase: number;
    swaySpeed: number;
    r: number;
    baseA: number;
    flicker: number;
    c: string;
  };

  let embers: Ember[] = [];
  let W = 0,
    H = 0;
  let count = 0;

  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // scale ember count to screen area so phones don't get overcrowded
    count = Math.round(DENSITY * (W * H) / (1920 * 1080));
    count = Math.max(count, 8);
  }
  resize();
  window.addEventListener('resize', resize);

  function rand(min: number, max: number) {
    return min + Math.random() * (max - min);
  }

  function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function makeEmber(startAnywhere: boolean): Ember {
    return {
      x: rand(0, W),
      y: startAnywhere ? rand(0, H) : H + rand(5, 40),
      vy: rand(SPEED_MIN, SPEED_MAX),
      sway: rand(0.2, 0.9), // horizontal drift strength
      phase: rand(0, Math.PI * 2),
      swaySpeed: rand(0.005, 0.02),
      r: rand(SIZE_MIN, SIZE_MAX),
      baseA: rand(OPACITY_MIN, OPACITY_MAX),
      flicker: rand(0.01, 0.05),
      c: pick(COLORS),
    };
  }

  for (let i = 0; i < count; i++) {
    embers.push(makeEmber(true));
  }

  let running = true;
  let rafId: number | null = null;

  function onVisibilityChange() {
    if (document.hidden) {
      running = false;
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    } else if (!running) {
      running = true;
      rafId = requestAnimationFrame(tick);
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  function tick() {
    if (!running) return;
    // Clear in CSS px (W,H) -- NOT canvas.width/height. The active setTransform(dpr)
    // scales this rect too, so CSS-px maps to the full device buffer at ANY dpr. Passing
    // the device dims here double-scales; when dpr < 1 (screen/browser zoom below 100%) it
    // under-clears, leaving an uncleared right/bottom band that smears embers into streaks.
    ctx.clearRect(0, 0, W, H);

    while (embers.length < count) embers.push(makeEmber(false));
    if (embers.length > count) embers.length = count;

    for (let i = 0; i < embers.length; i++) {
      const e = embers[i];
      e.phase += e.swaySpeed;
      e.y -= e.vy;
      e.x += Math.sin(e.phase) * e.sway * 0.3;
      if (e.y < -6) {
        embers[i] = makeEmber(false);
        continue;
      }
      // fade near the top of the screen so headers stay clean
      const fadeTop = Math.min(e.y / (H * FADE_TOP), 1);
      const a = e.baseA * fadeTop + Math.sin(e.phase * 3) * e.flicker;
      ctx.globalAlpha = Math.max(Math.min(a, 0.5), 0);
      ctx.fillStyle = e.c;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    rafId = requestAnimationFrame(tick);
  }
  rafId = requestAnimationFrame(tick);

  return function stopEmbers() {
    running = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    canvas.remove();
    embers = [];
  };
}
